/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState } from "react";
import { Input, Select, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { CheckOutlined, LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { updateTeamMembers } from "../../../redux/actions";
import { NumericInput } from "../../My Profile/components/NumericInput";
import { getCountryPhoneCode, getFullStateName } from "../../My Profile/components/helper";
import TopAlert from "../../../components/TopNotification/TopAlert";
import "./Add Member.css";

const { Option } = Select;

const ALL_PHONE_CODES = getCountryPhoneCode(null, true);
const COUNTRIES = Object.keys(ALL_PHONE_CODES)
  .map((name) => ({
    name,
    iso2: (getFullStateName(name) || "").toLowerCase(),
    code: ALL_PHONE_CODES[name],
  }))
  .filter((c) => c.iso2)
  .sort((a, b) => a.name.localeCompare(b.name));

// Strip a known country code prefix from the stored phone number.
// We default to US (+1) and strip only if the number starts with "1"
// followed by enough digits to be a local number. Keeping it simple
// per spec — more sophisticated detection is out of scope here.
function parsePhoneNumber(raw) {
  if (!raw) return { localNumber: "", flagcode: "us", countryCode: "1" };
  const digits = raw.replace(/\D/g, "");
  // US: starts with 1, local part is 10 digits (total 11)
  if (digits.startsWith("1") && digits.length === 11) {
    return { localNumber: digits.slice(1), flagcode: "us", countryCode: "1" };
  }
  // Fallback: return raw digits as-is with US defaults
  return { localNumber: digits, flagcode: "us", countryCode: "1" };
}

function Edit_Member({ member, setShowEditModal, onSuccess, onChangePassword }) {
  const invitationId = member?.invitationId || member?.InvitationId;
  const memberEmail = member?.EmailAddress || member?.emailAddress || member?.email || "";
  const rawPhone =
    member?.PhoneNumber ||
    member?.phoneNumber ||
    member?.MobileNumber ||
    member?.mobileNumber ||
    "";
  const parsed = parsePhoneNumber(rawPhone);

  const initialFullName = (() => {
    const first = member?.Profiles?.FirstName || member?.firstName || "";
    const last = member?.Profiles?.LastName || member?.lastName || "";
    if (first || last) return `${first} ${last}`.trim();
    return member?.FullName || member?.fullName || "";
  })();

  const [fullName, setFullName] = useState(initialFullName);
  const [jobTitle, setJobTitle] = useState(member?.JobTitle || member?.jobTitle || "");
  const [flagcode, setFlagcode] = useState(parsed.flagcode);
  const [CountryCodeValue, setCountryCodeValue] = useState(parsed.countryCode);
  const [MobilePhone, setPhoneNumber] = useState(parsed.localNumber);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();
  const updateLoading = useSelector((state) => state.updateTeamMembersReducer.loading);
  const updateSuccess = useSelector((state) => state.updateTeamMembersReducer.success);
  const updateError = useSelector((state) => state.updateTeamMembersReducer.error);

  // Watch for update outcome. No reset action exists for this reducer,
  // so we track the transition from false → true via a local flag.
  const [wasLoading, setWasLoading] = useState(false);

  useEffect(() => {
    if (updateLoading) {
      setWasLoading(true);
    }
    if (wasLoading && !updateLoading) {
      if (updateSuccess) {
        setSuccess(true);
        onSuccess?.();
        setTimeout(() => setShowEditModal(false), 2000);
      }
      setWasLoading(false);
      // Error case: updateError is true — user sees nothing extra beyond
      // the TopAlert not showing; a future improvement could surface an error banner.
    }
  }, [updateLoading, updateSuccess, updateError, onSuccess, setShowEditModal, wasLoading]);

  function handleCountryChange(iso2) {
    const country = COUNTRIES.find((c) => c.iso2 === iso2);
    if (country) {
      setFlagcode(country.iso2);
      setCountryCodeValue(country.code);
    }
  }

  function validate() {
    const errs = {};
    const namePattern = /^[a-zA-Z]{2,40}( [a-zA-Z]{2,40})+$/;

    if (!fullName.trim()) {
      errs.fullName = "Please add full name";
    } else if (!namePattern.test(fullName.trim())) {
      errs.fullName = "Enter first and last name separated by a space";
    }

    if (!jobTitle.trim()) {
      errs.jobTitle = "Please add job title";
    }

    const digits = MobilePhone.replace(/\D/g, "");
    if (!MobilePhone.trim()) {
      errs.phone = "Please add phone number";
    } else if (digits.length < 7) {
      errs.phone = "Please enter a valid phone number.";
    }

    return errs;
  }

  function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (!invitationId) {
      message.error("Invitation id not found");
      return;
    }
    setErrors({});
    setWasLoading(false); // reset transition tracker before new dispatch
    dispatch(
      updateTeamMembers(invitationId, {
        FullName: fullName,
        JobTitle: jobTitle,
        PhoneNumber: `${CountryCodeValue}${MobilePhone}`,
      })
    );
  }

  const flagselection = (
    <Select
      value={flagcode}
      onChange={handleCountryChange}
      className="am-country-dropdown"
      dropdownMatchSelectWidth={false}
      bordered={false}
      showSearch
      optionFilterProp="label"
      filterOption={(input, option) =>
        option?.label?.toLowerCase().includes(input.toLowerCase())
      }
    >
      {COUNTRIES.map((c) => (
        <Option key={c.iso2} value={c.iso2} label={`${c.name} +${c.code}`}>
          <div className="am-country-option">
            <img
              src={`https://flagcdn.com/w40/${c.iso2}.png`}
              alt={c.name}
              className="am-flag-img"
            />
            <span className="roleExperience-Mobile-code">+{c.code}</span>
          </div>
        </Option>
      ))}
    </Select>
  );

  return (
    <div className="am-form">
      {success && (
        <TopAlert
          display="block"
          title="Member updated successfully"
          type="success"
        />
      )}
      <h2 className="am-title">Edit Member</h2>
      <hr className="am-divider" />

      {/* Full Name */}
      <div className="am-field">
        <label className="am-label">
          Full Name <span className="am-required">*</span>
        </label>
        <Input
          className={`am-input ${errors.fullName ? "am-input-error" : ""}`}
          placeholder="Enter full name"
          maxLength={50}
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            setErrors((prev) => ({ ...prev, fullName: "" }));
          }}
        />
        <div className="am-input-footer">
          {errors.fullName ? (
            <span className="am-err-text">{errors.fullName}</span>
          ) : (
            <span />
          )}
          <span className="am-counter">{fullName.length}/50</span>
        </div>
      </div>

      {/* Work Email — read-only */}
      <div className="am-field">
        <label className="am-label">Work Email</label>
        <Input
          className="am-input am-email-disabled"
          value={memberEmail}
          disabled
        />
      </div>

      {/* Job Title */}
      <div className="am-field">
        <label className="am-label">
          Job Title <span className="am-required">*</span>
        </label>
        <Input
          className={`am-input ${errors.jobTitle ? "am-input-error" : ""}`}
          placeholder="Enter job title"
          value={jobTitle}
          onChange={(e) => {
            setJobTitle(e.target.value);
            setErrors((prev) => ({ ...prev, jobTitle: "" }));
          }}
        />
        {errors.jobTitle && <span className="am-err-text">{errors.jobTitle}</span>}
      </div>

      {/* Mobile Number */}
      <div className="am-field">
        <label className="am-label">
          Mobile Number <span className="am-required">*</span>
        </label>
        <NumericInput
          className={`number-div ${errors.phone ? "am-input-error" : ""}`}
          flagselection={flagselection}
          value={MobilePhone}
          onChange={(val) => {
            setPhoneNumber(val);
            setErrors((prev) => ({ ...prev, phone: "" }));
          }}
          changesflag={() => {}}
          setcustomMessage={() => {}}
        />
        <div className="am-input-footer">
          {errors.phone ? (
            <span className="am-err-text">{errors.phone}</span>
          ) : (
            <span />
          )}
          <span className="am-counter">{MobilePhone.replace(/\D/g, "").length}/13</span>
        </div>
      </div>

      {/* Change Password */}
      <div className="am-field">
        <button
          type="button"
          className="am-change-pwd-btn"
          onClick={onChangePassword}
        >
          <SyncOutlined />
          Change Password
        </button>
      </div>

      {/* Save changes */}
      <button
        type="button"
        className="am-submit-btn"
        onClick={handleSubmit}
        disabled={updateLoading}
      >
        {updateLoading ? (
          <>
            <LoadingOutlined /> Saving...
          </>
        ) : (
          <>
            <CheckOutlined /> Save changes
          </>
        )}
      </button>
    </div>
  );
}

export default Edit_Member;
